import { Container } from "@mui/material";
import { styled } from "@mui/system";
import React from "react";
import { trackEvent } from "src/Amplitude";
import Layout from "src/components/core/Layout";
import ReferLegalDialog from "src/components/legal/ReferLegalDialog";
import EarningsGraph from "src/components/raf/EarningsGraph/EarningsGraph";
import InfoBlock from "src/components/raf/ReferralInfo/InfoBlock";
import ReferralList from "src/components/raf/ReferralInfo/ReferralList";
import TierBreakdown from "src/components/raf/ReferralInfo/TierBreakdown";
import TierProgessBanner from "src/components/raf/ReferralInfo/TierProgessBanner";
import useRafCalculations from "src/components/raf/extra/useRafCalculations";
import api from "src/store/features/api";
import { useUserSlice } from "src/store/features/user.slice";
import { v4 as uuidv4 } from "uuid";
import { Redirect } from "react-router-dom";
import useIsUkUserCountry from "src/utils/hooks/useIsUkUserCountry";

interface RaFPageWrapperProps {
  isLoading?: boolean;
  show?: boolean;
}

const RaFPageWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  flex: "1 0 0",
  alignSelf: "stretch",
}));

interface RaFPageTitleProps {
  color?: string;
}
const RaFPageTitleWrapper = styled("div")(() => ({
  display: "flex",
  width: "100%",
  padding: "18px 0px 8px 0px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
}));

const RaFPageTitle = styled("span")<RaFPageTitleProps>(({ color }) => ({
  color: color ?? "#FC4C4C",
  fontFamily: "Roobert",
  fontSize: "28px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "34px",
  letterSpacing: "-0.56px",
}));

const TierProgessBannerWrapper = styled("div")<RaFPageWrapperProps>(({ isLoading }) => ({
  width: "100%",
  animation: "skeleton-loading 1.5s infinite",
  background: isLoading ? "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)" : "none",
  backgroundSize: "200% 100%",

  div: {
    visibility: isLoading ? "hidden" : "visible",

    // ANIMATION
    "@keyframes skeleton-loading": {
      " 0%": { backgroundPosition: "200% 0" },
      "100% ": { backgroundPosition: "-200% 0" },
    },
  },
}));

const RaFPageBottomWrapper = styled("div")<RaFPageWrapperProps>(({ theme, isLoading }) => ({
  display: "flex",
  padding: isLoading ? "0px" : "0px 24px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  paddingBottom: "16px",
  alignSelf: "stretch",

  [theme.breakpoints.down(900)]: {
    padding: "0px 0px",
  },
}));

const TierWrapper = styled("div")(() => ({
  display: "flex",
  padding: "12px 0px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "24px",
  alignSelf: "stretch",
}));

const RaFPageReferralInfoWrapper = styled("div")<RaFPageWrapperProps>(({ isLoading, show }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  alignSelf: "stretch",
  flexWrap: "wrap",

  animation: "skeleton-loading 1.5s infinite",
  background: isLoading ? "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)" : "none",
  backgroundSize: "200% 100%",
  div: {
    // using opactiy to hide the div otherwise, visibility will interfere with a child element
    opacity: isLoading || !show ? 0 : 1,

    // ANIMATION
    "@keyframes skeleton-loading": {
      " 0%": { backgroundPosition: "200% 0" },
      "100% ": { backgroundPosition: "-200% 0" },
    },
  },
}));

const ReferralListWrapper = styled("div")<RaFPageWrapperProps>(({ isLoading, show }) => ({
  width: "100%",
  animation: "skeleton-loading 1.5s infinite",
  background: isLoading ? "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)" : "none",
  backgroundSize: "200% 100%",

  div: {
    visibility: isLoading || !show ? "hidden" : "visible",
    // ANIMATION
    "@keyframes skeleton-loading": {
      " 0%": { backgroundPosition: "200% 0" },
      "100% ": { backgroundPosition: "-200% 0" },
    },
  },
}));

const TierWrapperTitle = styled("div")<RaFPageWrapperProps>(({ isLoading, show }) => ({
  display: isLoading || !show ? "none" : "flex",
  alignItems: "center",
  gap: "5px",
  alignSelf: "stretch",
  color: "#002035",
  flex: "1 0 0",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
}));

const TermsLink = styled("span")(() => ({
  color: "#FC4C4C",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
  textDecorationLine: "underline",
  cursor: "pointer",
}));

const TiersList = styled("div")(() => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "16px",
  alignSelf: "stretch",
  flexWrap: "wrap",
}));

const TiersGrid = styled("div")<RaFPageWrapperProps>(({ theme, isLoading, show }) => ({
  width: "269px",
  flex: "1",

  animation: "skeleton-loading 1.5s infinite",
  background: isLoading ? "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)" : "none",
  backgroundSize: "200% 100%",
  div: {
    visibility: isLoading || !show ? "hidden" : "visible",
    // ANIMATION
    "@keyframes skeleton-loading": {
      " 0%": { backgroundPosition: "200% 0" },
      "100% ": { backgroundPosition: "-200% 0" },
    },
  },

  [theme.breakpoints.down(1235)]: {
    flex: "1 1 calc(50% - 20px)" /* Each child takes up half the width with spacing */,
  },
}));

const RaF = () => {
  const isUkUser = useIsUkUserCountry();
  const [useDefaultTheme, toggle] = React.useReducer((theme: any) => !theme, true);

  const {
    raf: { tier, referredAmount, OLD_RAF_SYSTEM, isLoadingRaf },
    details: { customerId, name },
    isUnverified,
  } = useUserSlice();

  // get common RaF calculations, string, text etc..
  const { referredInfoText } = useRafCalculations(tier, referredAmount);

  const { data: referralRewards, isLoading: isLoadingReferralsRewards } =
    api.useGetReferralRewardsQuery(undefined, { skip: isUkUser });
  const rewardsList = React.useMemo(() => {
    if (!referralRewards) return [];

    return referralRewards.map((reward) => ({
      id: uuidv4(),
      reward: reward.reward,
      timestamp: reward.timestamp,
      tier: reward.tier,
    }));
  }, [referralRewards]);

  const isLoading = React.useMemo(() => {
    if (isLoadingReferralsRewards || !name || isLoadingRaf) return true;
    return false;
  }, [isLoadingReferralsRewards, name, isLoadingRaf]);

  // TERMS OF RaF CODE
  const [openReferLegal, setOpenReferLegal] = React.useState<boolean>(false);
  // eslint-disable-next-line
  const [fetchUrlError, setFetchUrlError] = React.useState<boolean>(false);
  const handleOpenTermsOfRaF = () => {
    setOpenReferLegal(true);
  };
  const handleCloseReferLegal = () => {
    setOpenReferLegal(false);
  };

  React.useEffect(() => {
    if (isUkUser) return;
    trackEvent("Visit_Page", { page: "RaF" }, "RaF");
  }, [isUkUser]);

  // const isOctober2025 = new Date().getFullYear() === 2025 && new Date().getMonth() === 9;

  if (isUkUser) {
    return <Redirect to="/portfolio" />;
  }

  return (
    <Layout toggleTheme={toggle} useDefaultTheme={useDefaultTheme}>
      <Container>
        <RaFPageWrapper>
          <RaFPageTitleWrapper>
            <RaFPageTitle>
              Refer a friend to <RaFPageTitle color="#002035">Caleb & Brown</RaFPageTitle>
            </RaFPageTitle>
          </RaFPageTitleWrapper>
          {/* RaF Progress Banner */}
          <TierProgessBannerWrapper isLoading={isLoading}>
            <TierProgessBanner name={name} customerID={customerId} />
          </TierProgessBannerWrapper>

          <RaFPageBottomWrapper>
            <TierWrapper>
              {/* Oct 2025 promotion terms */}
              {/* <TierWrapperTitle
                isLoading={isLoading}
                show={isOctober2025}
                sx={{ display: "block" }}>
                For the month of October 2025, earn {""}
                <span style={{ fontWeight: "600" }}>double</span> the Referral Rewards! <br />
                By participating in this October Refer A Friend Promotion, you agree to be bound by
                the{" "}
                <TermsLink
                  onClick={() => {
                    trackEvent("User Opened Oct 2025 Promotion Terms", {}, "RaF");
                    window.open("http://calebandbrown.com/refer-a-friend-promotion", "_blank");
                  }}>
                  Promotion Terms and conditions
                </TermsLink>
              </TierWrapperTitle> */}

              <TierWrapperTitle isLoading={isLoading} show={!OLD_RAF_SYSTEM || isUnverified}>
                Learn more about our{" "}
                <TermsLink onClick={handleOpenTermsOfRaF}>
                  {" "}
                  Refer A Friend Terms and Conditions
                </TermsLink>
              </TierWrapperTitle>

              <TiersList>
                <TiersGrid isLoading={isLoading} show={!OLD_RAF_SYSTEM || isUnverified}>
                  <TierBreakdown tier="bronze" />
                </TiersGrid>
                <TiersGrid isLoading={isLoading} show={!OLD_RAF_SYSTEM || isUnverified}>
                  <TierBreakdown tier="silver" />
                </TiersGrid>
                <TiersGrid isLoading={isLoading} show={!OLD_RAF_SYSTEM || isUnverified}>
                  <TierBreakdown tier="gold" />
                </TiersGrid>
                <TiersGrid isLoading={isLoading} show={!OLD_RAF_SYSTEM || isUnverified}>
                  <TierBreakdown tier="black" />
                </TiersGrid>
              </TiersList>
            </TierWrapper>

            <RaFPageReferralInfoWrapper
              isLoading={isLoading}
              show={!OLD_RAF_SYSTEM || isUnverified}>
              {/* RaF Tier Info */}
              <InfoBlock
                title="You referred"
                highlightText={referredAmount.toString()}
                boldText="trading clients"
                infoText={referredInfoText}
              />
              {/* RaF Earnings Graph */}
              <EarningsGraph />
            </RaFPageReferralInfoWrapper>

            {/* RaF Referrals List*/}
            <ReferralListWrapper isLoading={isLoading} show={!OLD_RAF_SYSTEM || isUnverified}>
              <ReferralList list={rewardsList} />
            </ReferralListWrapper>
          </RaFPageBottomWrapper>
        </RaFPageWrapper>
      </Container>

      <ReferLegalDialog
        refetchUserQuery={() => setOpenReferLegal(false)}
        optIn={openReferLegal}
        setOptIn={setOpenReferLegal}
        setFetchUrlError={setFetchUrlError}
        closeButton
        onClose={handleCloseReferLegal}
      />
    </Layout>
  );
};

export default RaF;
